// Users API — резолв имён пользователей (id → displayName) и живой поиск для picker'ов.
// R36b / U-2 + U-15: транспорт — GET /api/users/lookup (R36a, isAuthenticated):
//   - lookup(q)     — живой поиск для UserAutocomplete (?q=, active-only, limit ≤20);
//   - lookupByIds   — batch-резолв для useUserMap (?ids=, ≤100 id, без фильтра по status).
// Legacy-режимы (GET /api/users, admin-only) удалены: useUserMap требует явный ids,
// все вызовы с ids ещё с R23 — иначе не-admin получал 403 и UI рисовал короткий UUID (U-15).

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import { t } from '@/i18n';

/**
 * Усечённая ссылка на пользователя (backend UserLookupDto, R36a).
 * Безопасна для всех аутентифицированных: без ролей/департаментов/статуса.
 */
export interface UserLookupDto {
  id: string;
  username: string;
  email: string;
  displayName: string;
}

/** Доп. контакт пользователя (backend ContactEntry: {"type", "value"}, V29 contacts_json). */
export interface ContactEntry {
  type: string;
  value: string;
}

/** Вложенный view роли (backend UserDto.RoleView). */
export interface RoleView {
  id: string;
  code: string;
  displayName: string;
}

/** Вложенный view департамента (backend UserDto.DepartmentView; name/code могут быть null). */
export interface DepartmentView {
  id: string;
  name: string | null;
  code: string | null;
  roleInDepartment: string;
}

/**
 * Полный DTO пользователя (backend UserDto, R37a/U-4).
 * Статус — как на проводе (UserStatus.toDb() → lowercase 'active'/'locked'/'disabled').
 */
export interface UserDto {
  id: string;
  username: string;
  email: string;
  displayName: string;
  status: string;
  phone: string | null;
  position: string | null;
  description: string | null;
  contacts: ContactEntry[];
  roles: RoleView[];
  departments: DepartmentView[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Request body {@code PATCH /api/users/me} (R39a/U-10, §5.7).
 * Все поля опциональны (null = «не менять»); expectedUpdatedAt — optimistic lock (nullable).
 * Смена email/username/password через этот endpoint невозможна по дизайну backend.
 */
export interface UserProfileUpdateRequest {
  displayName?: string | null;
  phone?: string | null;
  position?: string | null;
  description?: string | null;
  contacts?: ContactEntry[] | null;
  expectedUpdatedAt?: string | null;
}

/**
 * Request body {@code POST /api/users} (админ-создание, R40/U-11).
 * Контракт backend {@code UserCreateRequest} — только username/email/password/displayName
 * (назначение ролей/департаментов — отдельные admin-endpoint'ы, не вдаётся в этот body).
 */
export interface UserCreateRequest {
  username: string;
  email: string;
  password: string;
  displayName: string;
}

/**
 * Request body {@code PUT /api/users/{id}} (админ, R40/U-11: статус-реквизиты).
 * Все поля опциональны (null = не менять); status: 'active' | 'locked' | 'disabled'.
 */
export interface UserAdminUpdateRequest {
  email?: string | null;
  displayName?: string | null;
  status?: string | null;
  phone?: string | null;
  position?: string | null;
  description?: string | null;
  contacts?: ContactEntry[] | null;
}

/** Дефолтный limit живого поиска (backend cap для ?q= — 20, §5.4 ТЗ). */
const LOOKUP_DEFAULT_SIZE = 20;

/** staleTime для users-map: справочник меняется редко. */
const USERS_STALE_TIME_MS = 5 * 60 * 1000;

/** Backend cap для batch-резолва (UserController.LOOKUP_MAX_IDS); >100 → 400 (B2). */
const LOOKUP_MAX_IDS = 100;

/** GET /api/users/lookup?ids=<csv> для одного чанка (≤100 id). */
async function fetchLookupChunk(ids: string[]): Promise<UserLookupDto[]> {
  const { data } = await apiClient.get<UserLookupDto[]>('/api/users/lookup', {
    params: { ids: ids.join(',') },
  });
  return data;
}

/** Users API (lookup endpoint, isAuthenticated). */
export const usersApi = {
  /**
   * GET /api/users/lookup?q=<str>&size=<n> → UserLookupDto[] (U-2).
   * Живой поиск по username/email/displayName (ILIKE, status='active', limit ≤ 20).
   */
  async lookup(q: string, size: number = LOOKUP_DEFAULT_SIZE): Promise<UserLookupDto[]> {
    const { data } = await apiClient.get<UserLookupDto[]>('/api/users/lookup', {
      params: { q, size },
    });
    return data;
  },

  /**
   * GET /api/users/lookup?ids=<csv> → UserLookupDto[] (U-15).
   * Batch-резолв по id (≤100 на запрос — backend cap), без фильтра по status —
   * резолвятся и locked/disabled (их имена показываются в истории/аудите).
   * <p>
   * B2: ids > 100 чанкуются по {@link LOOKUP_MAX_IDS}, результаты сливаются
   * (иногда страница задач аудита содержит более 100 id).
   */
  async lookupByIds(ids: string[]): Promise<UserLookupDto[]> {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += LOOKUP_MAX_IDS) {
      chunks.push(ids.slice(i, i + LOOKUP_MAX_IDS));
    }
    const results = await Promise.all(chunks.map((chunk) => fetchLookupChunk(chunk)));
    return results.flat();
  },

  /**
   * PATCH /api/users/me → UserDto (R39a/U-10): self-service обновление профиля.
   * Доступен всем аутентифицированным (method-level isAuthenticated()).
   */
  async updateMe(payload: UserProfileUpdateRequest): Promise<UserDto> {
    const { data } = await apiClient.patch<UserDto>('/api/users/me', payload);
    return data;
  },

  /**
   * GET /api/users → UserDto[] (admin-only, R40/U-11).
   * Полный реестр с ролями/департаментами/статусом — только для админ-раздела «Команда».
   */
  async listAll(): Promise<UserDto[]> {
    const { data } = await apiClient.get<UserDto[]>('/api/users');
    return data;
  },

  /**
   * POST /api/users → UserDto (admin-only, R40/U-11).
   * Создание пользователя по контракту UserCreateRequest (username/email/password/displayName).
   */
  async createUser(payload: UserCreateRequest): Promise<UserDto> {
    const { data } = await apiClient.post<UserDto>('/api/users', payload);
    return data;
  },

  /**
   * PUT /api/users/{id} → UserDto (admin-only, R40/U-11).
   * Используется для смены статуса (деактивация/активация) по контракту UserUpdateRequest.
   */
  async updateUser(id: string, payload: UserAdminUpdateRequest): Promise<UserDto> {
    const { data } = await apiClient.put<UserDto>(`/api/users/${id}`, payload);
    return data;
  },
};

/**
 * Строит Map id → отображаемое имя из ответа lookup.
 * <p>
 * R41 / U-15: цепочка резолва — email → username (login) → displayName
 * (verbatim §6: «только email / login / displayName в этой приоритетности»).
 * displayName может дублироваться между пользователями, email — уникален.
 */
export function buildUserMap(users: UserLookupDto[]): Map<string, string> {
  return new Map(users.map((u) => [u.id, u.email || u.username || u.displayName]));
}

/**
 * Резолвит id пользователя в отображаемое имя.
 * <p>
 * B2 (pintest-fix-B): fallback «короткий UUID» удалён — неизвестный id или
 * незагруженная map (loading/error) даёт {@code t('common.unknown')} = «Неизвестно»,
 * а не нечитаемый обрезок UUID. null → «—».
 */
export function resolveUserName(
  userMap: Map<string, string> | undefined,
  userId: string | null | undefined,
): string {
  if (!userId) return '—';
  return userMap?.get(userId) ?? t('common.unknown');
}

/**
 * TanStack Query hook: Map<id, displayName> для резолва имён пользователей.
 * <p>
 * R36b / U-15: транспорт — {@code GET /api/users/lookup?ids=...} (isAuthenticated),
 * доступен всем ролям (раньше admin-only GET /api/users → 403 у manager → короткий UUID).
 * Пустой массив → пустая Map без запроса. Legacy-режим без ids удалён (R36b).
 */
export function useUserMap(ids: string[]): Map<string, string> | undefined {
  const uniqueSortedIds = [...new Set(ids)].sort();
  const isEmpty = uniqueSortedIds.length === 0;
  const query = useQuery({
    queryKey: ['users-lookup', 'ids', uniqueSortedIds],
    queryFn: () => usersApi.lookupByIds(uniqueSortedIds),
    enabled: !isEmpty,
    staleTime: USERS_STALE_TIME_MS,
    select: buildUserMap,
    retry: false,
  });
  if (isEmpty) return new Map();
  return query.data;
}
